import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import NewReaderOverlay from '../../../NewReaderOverlay';
import { WatermarkTrackingObject } from '../NewBasisReaderOverlay';
import UnlockPremium from '../UnlockPremium';
import { ampliConversionPath } from '../../../../../../constants';
import { selectUser } from '../../../../../../reducers/user.selectors';
import { useSelector } from 'react-redux';

interface DownloadLimitExceededOverlayProps {
  trackingObject: WatermarkTrackingObject;
  onClose?: () => void;
  hasDocumentLevelAccess?: boolean;
}

const DownloadLimitExceededOverlay: React.FC<DownloadLimitExceededOverlayProps> = (props) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);

  const getOverlayProps = () => {
    if (!user?.canUpgrade) {
      if (user?.isCourseUser && user?.isSchoolUser) {
        return {
          buttonEmoji: '',
          buttonText: t('Learn more'),
          bodyText: t('Your current subscription does not include access to our school library.' + 
            ' Find out how you can activate it.'),
        };
      }

      return {
        bodyText: t('To download this document, please contact the support team for an upgrade to meinUnterricht 360.'),
      };
    }

    if (user?.isSchoolUser) {
      return {
        buttonEmoji: '',
        buttonText: t('Learn more'),
        bodyText: t('Your current subscription does not include access to our school library.' + 
          ' Find out how you can activate it.'),
      };
    }

    if (user?.isBasisUser || user?.isCourseUser) {
      return {
        buttonEmoji: '🚀',
        buttonText: t('Update Plan'),
        bodyText: t('Get meinUnterricht 360 now to download this document')
      };
    }

    return {
      buttonEmoji: '👀',
      buttonText: t('Discover meinUnterricht Premium'),
      bodyText: props.hasDocumentLevelAccess
        ? (
          <Trans i18nKey='Download limit exceeded for a premium user'>
            <p>
              Unfortunately this does not work at the moment,
              because you have already used up your 100 possible downloads this month. 😕
            </p>
            <p>
              Tip: It&#39;s best to add this content to your watchlist
              so that you can download it directly in the next calendar month!
            </p>
          </Trans>
        )
        : t('Get meinUnterricht Premium now to download this document')
    };
  };

  const overlayProps = getOverlayProps();

  return (
    <NewReaderOverlay emoji='📁' title={t('Download limit exceeded')} onClose={props.onClose}>
      <NewReaderOverlay.BodyText>
        {overlayProps.bodyText}
      </NewReaderOverlay.BodyText>
      {!props.hasDocumentLevelAccess && overlayProps.buttonText && <UnlockPremium
        emoji={overlayProps.buttonEmoji}
        text={overlayProps.buttonText}
        minditem_id={props.trackingObject.minditem_id}
        current_page={props.trackingObject.current_page}
        page_count={props.trackingObject.page_count}
        onClick={props.onClose}
        conversionPath={ampliConversionPath.DownloadLimitReached}
      />}
    </NewReaderOverlay>
  );
};

export default DownloadLimitExceededOverlay;
